import { FC } from 'react';
import urlJoin from 'url-join';

import { Button } from '@/Components/Button';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { ExclamationSvg } from '@/Components/Icons/ExclamationSvg';
import { LogoSvg } from '@/Components/Icons/LogoSvg';
import { FlowStatus } from '@/types/flow.type';
import { ModalView } from '@/Components/ModalView';
import { AppModalProps } from '@/types/modal.type';

type FlowPreviewModalProps = AppModalProps<{
    flowPage: string;
    flowCode: string;
    flowStatus: FlowStatus;
    workspaceUrl: string;
    workspaceInstallationVerifiedAt: boolean;
}>;

export const FlowPreviewModal: FC<FlowPreviewModalProps> = ({
    payload,
    onClose,
}) => {
    const flowPage = payload?.flowPage;
    const flowCode = payload?.flowCode;
    const flowStatus = payload?.flowStatus;
    const workspaceUrl = payload?.workspaceUrl;
    const workspaceInstallationVerifiedAt =
        payload?.workspaceInstallationVerifiedAt;

    const previewUrl = urlJoin(workspaceUrl || '', flowPage).toString();

    const isWarningNoteVisible = flowStatus !== 'published';

    return (
        <ModalView
            maxWidth="md"
            onClose={onClose}
            contentContainerClassName="py-4 text-brand-ocean">
            <div className="flex flex-row items-center justify-end">
                <button
                    title="Close"
                    aria-label="Close"
                    className="mx-4 p-2 transition-all rounded-sm hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            <div className="flex flex-row items-center justify-center">
                <LogoSvg className="mb-8 -mr-1.5" />
                <ExclamationSvg />
            </div>
            <p className="text-body-text text-center mx-4">
                The most recent Flow changes will be previewed on this URL, and
                open in a new window.
            </p>
            {isWarningNoteVisible ? (
                <p className="text-body-text text-center mx-4 mt-2">
                    The preview is unable to display example video content.
                </p>
            ) : null}
            <p className="text-large-body-text text-center mt-4">
                {previewUrl}
            </p>
            {!workspaceInstallationVerifiedAt ? (
                <p className="text-body-text text-orange-500 text-center mt-4">
                    The preview may not work correctly
                    <br />
                    as we are unable to verify the Install Code is present
                    <br />
                    on this URL.{' '}
                    <a
                        className="underline !text-body-text !text-orange-500"
                        href={route('settings.installation')}
                        target="_blank">
                        Visit Settings to verify
                    </a>
                </p>
            ) : null}
            <div className="flex flex-row items-center justify-center mt-6 mb-2">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    className="py-1.5 px-3 mr-4">
                    Cancel
                </Button>
                <Button
                    elementType="a"
                    target="_blank"
                    href={urlJoin(
                        previewUrl,
                        `?reelflow_preview=1&reelflow_flow=${flowCode}`,
                    ).toString()}
                    variant="success"
                    onClick={onClose}
                    className="py-1.5 px-3">
                    Open Preview
                </Button>
            </div>
        </ModalView>
    );
};
